import React, { useMemo, useState } from 'react';
import { X, Shield, Crown, UserPlus, LogOut, Archive, ArchiveRestore, Search } from 'lucide-react';
import { ChannelService } from '../../services/ChannelService';
import { ChannelGlyph } from './ChannelDirectoryModal';

const POLICY = { everyone: 'Everyone can post', admins: 'Only channel admins post' };
const MEMBERSHIP = { all_staff: 'Every staff member is in this channel automatically', manual: 'Members are added by channel admins' };

/**
 * Right-hand panel for a channel. Reads the live channel from useChannel (passed in);
 * every change goes through the proxy, which re-checks permission.
 */
const ChannelInfoPanel = ({ chatId, channel, uid, isAdmin, isOwner, allUsers = [], onClose, onLeft }) => {
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [adding, setAdding] = useState(false);
    const [query, setQuery] = useState('');
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({ name: '', purpose: '' });

    const userById = useMemo(() => new Map((allUsers || []).map(u => [u.id || u.uid, u])), [allUsers]);
    const members = useMemo(() => Object.keys(channel?.members || {})
        .filter(m => channel.members[m] === true)
        .map(m => ({
            uid: m,
            name: userById.get(m)?.name || 'Former staff member',
            role: userById.get(m)?.role || '',
            owner: channel.ownerId === m,
            admin: channel.ownerId === m || channel.admins?.[m] === true,
        }))
        .sort((a, b) => (b.owner - a.owner) || (b.admin - a.admin) || a.name.localeCompare(b.name)),
    [channel, userById]);

    const addable = useMemo(() => {
        if (!adding) return [];
        const q = query.trim().toLowerCase();
        return (allUsers || [])
            .filter(u => u && u.id && u.role && u.isActive !== false && channel?.members?.[u.id] !== true)
            .filter(u => !channel?.allowedRoles || channel.allowedRoles.includes(String(u.role).toLowerCase()))
            .filter(u => !q || `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q))
            .slice(0, 30);
    }, [adding, query, allUsers, channel]);

    const run = async (key, fn) => {
        setBusy(key); setError(null);
        try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(null); }
    };

    if (!channel) return null;
    const manual = channel.membership !== 'all_staff';

    return (
        <aside className="w-80 flex-shrink-0 h-full border-l border-slate-200 bg-white flex flex-col" aria-label="Channel details">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <h3 className="text-sm font-bold text-slate-800">Channel details</h3>
                <button onClick={onClose} className="p-1 rounded-full text-slate-400 hover:bg-slate-100" aria-label="Close details"><X size={16} /></button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 grid gap-4 content-start text-sm">
                {!editing ? (
                    <div>
                        <div className="flex items-center gap-2 text-base font-bold text-slate-800">
                            <ChannelGlyph kind={channel.kind} visibility={channel.visibility} className="text-indigo-500" /> {channel.name}
                        </div>
                        {channel.purpose && <p className="text-xs text-slate-500 mt-1 whitespace-pre-line">{channel.purpose}</p>}
                        {isAdmin && (
                            <button onClick={() => { setDraft({ name: channel.name || '', purpose: channel.purpose || '' }); setEditing(true); }}
                                className="mt-2 text-xs font-semibold text-indigo-600 hover:underline">Edit name and purpose</button>
                        )}
                    </div>
                ) : (
                    <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); run('edit', async () => { await ChannelService.update(chatId, draft); setEditing(false); }); }}>
                        <input required minLength={2} maxLength={60} value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm" aria-label="Channel name" />
                        <textarea rows={3} maxLength={300} value={draft.purpose} onChange={e => setDraft(d => ({ ...d, purpose: e.target.value }))}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm resize-none" aria-label="Channel purpose" />
                        <div className="flex gap-2">
                            <button type="submit" disabled={busy === 'edit'} className="text-xs font-semibold text-white bg-indigo-600 rounded-lg px-3 py-1.5">Save</button>
                            <button type="button" onClick={() => setEditing(false)} className="text-xs text-slate-500 px-2">Cancel</button>
                        </div>
                    </form>
                )}

                <ul className="text-xs text-slate-600 grid gap-1 bg-slate-50 rounded-xl p-3">
                    <li>{POLICY[channel.postingPolicy] || channel.postingPolicy}</li>
                    <li>{MEMBERSHIP[channel.membership] || ''}</li>
                    <li>{channel.visibility === 'open' ? 'Any staff member can find and join it' : 'Invite only'}</li>
                    {channel.allowedRoles && <li>Managers and admins only</li>}
                    {channel.reviewDate && <li>Review by {new Date(channel.reviewDate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</li>}
                    {channel.archived && <li className="font-semibold text-amber-700">Archived — read-only</li>}
                </ul>

                {error && <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2" role="alert">{error}</p>}

                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{members.length} members</span>
                        {isAdmin && manual && !channel.archived && (
                            <button onClick={() => setAdding(a => !a)} className="text-xs font-semibold text-indigo-600 flex items-center gap-1"><UserPlus size={13} /> Add</button>
                        )}
                    </div>

                    {adding && (
                        <div className="mb-2 rounded-xl border border-slate-200">
                            <div className="relative">
                                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Find people"
                                    className="w-full rounded-t-xl border-b border-slate-100 pl-8 pr-3 py-1.5 text-sm" aria-label="Find people to add" />
                            </div>
                            <div className="max-h-40 overflow-y-auto custom-scrollbar">
                                {addable.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No one to add.</p>}
                                {addable.map(u => (
                                    <button key={u.id} disabled={busy === `add-${u.id}`}
                                        onClick={() => run(`add-${u.id}`, () => ChannelService.changeMembers(chatId, { add: [u.id] }))}
                                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 flex justify-between">
                                        <span className="truncate">{u.name || u.email}</span><span className="text-[10px] text-slate-400">{u.role}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <ul className="grid gap-0.5">
                        {members.map(m => (
                            <li key={m.uid} className="group flex items-center gap-2 py-1.5">
                                <span className="truncate text-slate-700">{m.name}{m.uid === uid ? ' (you)' : ''}</span>
                                {m.owner && <Crown size={12} className="text-amber-500 flex-shrink-0" aria-label="Owner" />}
                                {!m.owner && m.admin && <Shield size={12} className="text-indigo-500 flex-shrink-0" aria-label="Admin" />}
                                <span className="ml-auto flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                                    {isOwner && !m.owner && (
                                        <button onClick={() => run(`adm-${m.uid}`, () => ChannelService.setAdmin(chatId, m.uid, !m.admin))}
                                            className="text-[10px] font-semibold text-indigo-600 hover:underline">{m.admin ? 'Remove admin' : 'Make admin'}</button>
                                    )}
                                    {isAdmin && manual && !m.owner && m.uid !== uid && (
                                        <button onClick={() => run(`rm-${m.uid}`, () => ChannelService.changeMembers(chatId, { remove: [m.uid] }))}
                                            className="text-[10px] font-semibold text-red-500 hover:underline">Remove</button>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="grid gap-2 pt-2 border-t border-slate-100">
                    {isAdmin && (
                        <button onClick={() => run('archive', () => ChannelService.update(chatId, { archived: !channel.archived }))}
                            className="flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-slate-800">
                            {channel.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                            {channel.archived ? 'Restore channel' : 'Archive channel (keeps all messages)'}
                        </button>
                    )}
                    {channel.leavable !== false && !isOwner && (
                        <button onClick={() => run('leave', async () => { await ChannelService.leave(chatId); onLeft?.(); })}
                            className="flex items-center gap-2 text-xs font-semibold text-red-500 hover:text-red-600">
                            <LogOut size={14} /> Leave channel
                        </button>
                    )}
                </div>
            </div>
        </aside>
    );
};

export default ChannelInfoPanel;
